import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { useForm } from "@tanstack/react-form";
import Form from "src/components/base/form";
import { ErrorMessage, Field, FieldGroup, Fieldset, Label, RequiredLabel } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import { Textarea } from "src/components/base/textarea";
import { ApiInternal } from "src/api/api_internal";

/**
 * The properties for {@link CreateRulesetDialog}
 */
export type CreateRulesetDialogProps = {
    /** Whether the dialog is open */
    isOpen: boolean;
    /** Callback when the form is submitted */
    onSubmit: () => void;
    /** Callback when the popup should be closed */
    onClose: () => void;
};

/**
 * A dialog to create a new tag
 */
export default function CreateRulesetDialog(props: CreateRulesetDialogProps) {
    const [t] = useTranslation("dialog-create-ruleset");
    const [tg] = useTranslation();

    const form = useForm({
        defaultValues: { name: "", description: "" },
        validators: {
            onSubmitAsync: async ({ value }) => {
                await ApiInternal.rulesets.create({
                    name: value.name,
                    description: value.description,
                });

                props.onSubmit();
            },
        },
    });

    useEffect(() => {
        form.reset();
    }, [props.isOpen]);

    return (
        <Dialog open={props.isOpen} onClose={props.onClose}>
            <DialogTitle>{t("heading.create-ruleset")}</DialogTitle>
            <DialogBody>
                <Form onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"name"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.name")}</RequiredLabel>
                                        <Input
                                            autoFocus={true}
                                            required={true}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        />
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>

                            <form.Field name={"description"}>
                                {(fieldApi) => (
                                    <Field>
                                        <Label>{t("label.description")}</Label>
                                        <Textarea
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            resizable={false}
                                        />
                                    </Field>
                                )}
                            </form.Field>

                            <DialogActions>
                                <Button plain={true} onClick={props.onClose}>
                                    {tg("button.cancel")}
                                </Button>
                                <PrimaryButton type={"submit"}>{t("button.create-ruleset")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
